'use client';

import { inter600 } from '@/config/fonts';
import IconContainer from '@/shared/components/IconContainer/IconContainer';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import ModalCloseLink from './ModalCloseLink';
import { selectOpenModals } from './redux/modals.selectors';

interface ModalHeaderProps {
  className?: string;
  closeAction?: 'url' | 'state';
  modalName: string;
  isFloating?: boolean;
  icon?: ReactNode;
  title?: string;
  children?: ReactNode;
}

const ModalHeader = ({
  className,
  modalName,
  isFloating,
  closeAction = 'url',
  icon,
  title,
  children,
}: ModalHeaderProps) => {
  const openModals = useSelector(selectOpenModals);
  const hasMultipleModals = openModals.length > 1;
  const hasHeaderContent = icon || title || children;

  return (
    <div className={classNames('modal-header relative z-10', className)}>
      <div
        className={classNames('flex items-center p-4', {
          'justify-end': !hasHeaderContent && !hasMultipleModals,
          'justify-between': hasHeaderContent,
          'absolute w-full': !!isFloating,
        })}>
        {/* Left side: Back button (if multiple modals) + Icon/Title/Children */}
        {(hasMultipleModals || hasHeaderContent) && (
          <div className="flex items-center gap-4">
            {hasMultipleModals && (
              <ModalCloseLink
                closeAction={closeAction}
                modalName={modalName}
                iconVariant="back"
                className="dark:bg-base-100 rounded-md bg-white"
              />
            )}
            {hasHeaderContent && (
              <div className="flex items-center gap-4">
                {icon && (
                  <IconContainer icon={icon} className="dark:bg-base-200 h-11 w-11" />
                )}
                {title && (
                  <div className={classNames('text-xl', inter600.className)}>{title}</div>
                )}
                {children}
              </div>
            )}
          </div>
        )}

        {/* Right side: Close button */}
        {!hasMultipleModals && (
          <ModalCloseLink
            closeAction={closeAction}
            modalName={modalName}
            iconVariant="close"
            className="dark:bg-base-100 rounded-md bg-white"
          />
        )}
      </div>
    </div>
  );
};

export default ModalHeader;
